/**
 * Page de détail d'un contact : fiche complète en scroll unique avec
 * timeline, To Dos et actions spontanées intégrés.
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getContact, updateContact, deleteContact, getContactTimeline, getContactTodos, getContactActions } from "../api/contactApi";
import { deleteTodo } from "../api/todoApi";
import { deleteAction } from "../api/spontaneousActionApi";
import StatusBadge from "../components/StatusBadge";
import ConfirmDialog from "../components/ConfirmDialog";
import InlineTodoForm from "../components/InlineTodoForm";
import InlineActionForm from "../components/InlineActionForm";
import { VISIBILITY_LABELS, STATUS_LABELS, STAKE_LABELS, URGENCY_LABELS } from "../utils/constants";
import { formatDate, isOverdue } from "../utils/dateUtils";
import "./ContactDetailPage.css";
import "./PageStyles.css";

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});

  const [timeline, setTimeline] = useState([]);
  const [todos, setTodos] = useState([]);
  const [actions, setActions] = useState([]);
  const [deleteTodoId, setDeleteTodoId] = useState(null);
  const [deleteActionId, setDeleteActionId] = useState(null);
  const [showNewTodo, setShowNewTodo] = useState(false);
  const [showNewAction, setShowNewAction] = useState(false);

  useEffect(() => {
    loadAll();
  }, [id]);

  async function loadAll() {
    try {
      const [contactData, timelineData, todosData, actionsData] = await Promise.all([
        getContact(id),
        getContactTimeline(id),
        getContactTodos(id),
        getContactActions(id),
      ]);
      setContact(contactData);
      setForm({ ...contactData });
      setTimeline(timelineData);
      setTodos(todosData);
      setActions(actionsData);
    } catch {
      setError("Contact introuvable.");
    }
  }

  // Droit de modification du contact (propriétaire, contact public, ou
  // squad/practice lead du propriétaire) — calculé côté backend.
  const canEdit = contact ? Boolean(contact.can_edit) : false;

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSave(e) {
    e.preventDefault();
    try {
      const updated = await updateContact(id, {
        last_name: form.last_name,
        first_name: form.first_name,
        company: form.company,
        job_title: form.job_title,
        email: form.email,
        phone: form.phone,
        comments: form.comments,
        visibility: form.visibility,
      });
      setContact(updated);
      setEditing(false);
      setError("");
    } catch {
      setError("Erreur lors de l'enregistrement.");
    }
  }

  async function handleDeleteContact() {
    await deleteContact(id);
    navigate("/contacts");
  }

  async function handleDeleteTodo() {
    await deleteTodo(deleteTodoId);
    setDeleteTodoId(null);
    loadAll();
  }

  async function handleDeleteAction() {
    await deleteAction(deleteActionId);
    setDeleteActionId(null);
    loadAll();
  }

  if (error && !contact) {
    return <div className="page"><div className="error-message">{error}</div></div>;
  }
  if (!contact) return <div className="page"><p>Chargement...</p></div>;

  const fullName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "Contact";

  return (
    <div className="page">
      <div className="page-header">
        <h2>{fullName}</h2>
        <Link to="/contacts" className="btn btn-secondary">Retour aux contacts</Link>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* === FICHE CONTACT === */}
      <div className="contact-detail-card">
        {editing ? (
          <form onSubmit={handleSave}>
            <div className="form-row">
              <div className="form-group">
                <label>Nom</label>
                <input name="last_name" value={form.last_name || ""} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Prénom</label>
                <input name="first_name" value={form.first_name || ""} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Société</label>
                <input name="company" value={form.company || ""} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Fonction</label>
                <input name="job_title" value={form.job_title || ""} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input name="email" type="email" value={form.email || ""} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Téléphone</label>
                <input name="phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone || ""} onChange={handleChange} />
              </div>
            </div>
            <div className="form-group">
              <label>Commentaires</label>
              <textarea name="comments" value={form.comments || ""} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Visibilité</label>
              <select name="visibility" value={form.visibility} onChange={handleChange}>
                <option value="private">Privé</option>
                <option value="public">Public</option>
              </select>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Enregistrer</button>
              <button type="button" className="btn btn-secondary" onClick={() => { setEditing(false); setForm({ ...contact }); }}>
                Annuler
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="detail-grid">
              <DetailField label="Owner" value={contact.owner_name} />
              <DetailField label="Nom" value={contact.last_name} />
              <DetailField label="Prénom" value={contact.first_name} />
              <DetailField label="Société" value={contact.company} />
              <DetailField label="Fonction" value={contact.job_title} />
              <DetailField label="Email" value={contact.email} isLink />
              <DetailField label="Téléphone" value={contact.phone} />
            </div>
            <div className="detail-section">
              <span className="detail-label">Visibilité</span>
              <StatusBadge value={contact.visibility} label={VISIBILITY_LABELS[contact.visibility]} />
            </div>
            {contact.comments && (
              <div className="detail-section">
                <span className="detail-label">Commentaires</span>
                <p className="detail-comments">{contact.comments}</p>
              </div>
            )}
            {canEdit && (
              <div className="form-actions" style={{ marginTop: "1.25rem" }}>
                <button className="btn btn-primary" onClick={() => setEditing(true)}>
                  Modifier
                </button>
                <button className="btn btn-danger" onClick={() => setShowDelete(true)}>
                  Supprimer
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* === CHRONOLOGIE === */}
      <div className="contact-section">
        <h3 className="section-title">Chronologie</h3>
        {timeline.length === 0 ? (
          <p className="section-empty">Aucun événement pour ce contact.</p>
        ) : (
          <div className="timeline">
            {timeline.map((item) => (
              <div key={`${item.type}-${item.id}`} className={`timeline-item timeline-${item.type}`}>
                <div className="timeline-marker" />
                <div className="timeline-content">
                  <div className="timeline-header">
                    <span className="timeline-date">{formatDate(item.date) || "Sans date"}</span>
                    <span className={`timeline-type-badge ${item.type}`}>
                      {item.type === "todo" ? "To Do" : "Action Réalisée"}
                    </span>
                    {item.type === "todo" && (
                      <StatusBadge value={item.status} label={STATUS_LABELS[item.status]} />
                    )}
                    {item.type === "todo" && item.status === "in_progress" && isOverdue(item.date) && (
                      <StatusBadge value="overdue" label="En retard" variant="danger" />
                    )}
                    <span className="timeline-responsable">
                      Responsable : {item.responsable_name || "—"}
                    </span>
                  </div>
                  <p className="timeline-description">{item.description || "—"}</p>
                  {item.tags?.length > 0 && (
                    <div className="tag-list">
                      {item.tags.map((t) => (
                        <span key={t.id} className="tag-inline">{t.label}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* === TO DOS === */}
      <div className="contact-section">
        <div className="section-header">
          <h3 className="section-title">To Dos ({todos.length})</h3>
          {canEdit && (
            <button className="btn btn-primary btn-small" onClick={() => setShowNewTodo(!showNewTodo)}>
              {showNewTodo ? "Annuler" : "Nouveau To Do"}
            </button>
          )}
        </div>
        {showNewTodo && (
          <InlineTodoForm
            contactId={Number(id)}
            onCreated={() => { setShowNewTodo(false); loadAll(); }}
            onCancel={() => setShowNewTodo(false)}
          />
        )}
        {todos.length === 0 ? (
          <p className="section-empty">Aucun To Do pour ce contact.</p>
        ) : (
          <div className="inline-table-wrapper">
            <table className="sortable-table">
              <thead>
                <tr>
                  <th>Créateur</th>
                  <th>Description</th>
                  <th>Responsable</th>
                  <th>Échéance</th>
                  <th>Enjeu</th>
                  <th>Urgence</th>
                  <th>Tags</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {todos.map((t) => {
                  const overdue = t.status === "in_progress" && isOverdue(t.due_date);
                  return (
                    <tr key={t.id}>
                      <td>{t.owner_name || "—"}</td>
                      <td title={t.description}>
                        {(t.description || "").substring(0, 60)}
                        {(t.description || "").length > 60 ? "..." : ""}
                      </td>
                      <td>{t.responsable_name || "—"}</td>
                      <td>
                        {formatDate(t.due_date)}
                        {overdue && <StatusBadge value="overdue" label="En retard" variant="danger" />}
                      </td>
                      <td>{t.stake_level && <StatusBadge value={t.stake_level} label={STAKE_LABELS[t.stake_level]} />}</td>
                      <td>{t.urgency_level && <StatusBadge value={t.urgency_level} label={URGENCY_LABELS[t.urgency_level]} />}</td>
                      <td>
                        <div className="tag-list">
                          {t.tags?.map((tag) => (
                            <span key={tag.id} className="tag-inline">{tag.label}</span>
                          ))}
                        </div>
                      </td>
                      <td className="row-actions">
                        {canEdit && (
                          <>
                            <button className="btn btn-small" onClick={() => navigate(`/todos/${t.id}/edit`)}>Modifier</button>
                            <button className="btn btn-small btn-danger" onClick={() => setDeleteTodoId(t.id)}>Supprimer</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* === ACTIONS SPONTANÉES === */}
      <div className="contact-section">
        <div className="section-header">
          <h3 className="section-title">Actions Réalisées ({actions.length})</h3>
          {canEdit && (
            <button className="btn btn-primary btn-small" onClick={() => setShowNewAction(!showNewAction)}>
              {showNewAction ? "Annuler" : "Nouvelle action"}
            </button>
          )}
        </div>
        {showNewAction && (
          <InlineActionForm
            contactId={Number(id)}
            onCreated={() => { setShowNewAction(false); loadAll(); }}
            onCancel={() => setShowNewAction(false)}
          />
        )}
        {actions.length === 0 ? (
          <p className="section-empty">Aucune action réalisée pour ce contact.</p>
        ) : (
          <div className="inline-table-wrapper">
            <table className="sortable-table">
              <thead>
                <tr>
                  <th>Créateur</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Responsable</th>
                  <th>Tags</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {actions.map((a) => {
                  return (
                    <tr key={a.id}>
                      <td>{a.owner_name || "—"}</td>
                      <td>{formatDate(a.action_date)}</td>
                      <td title={a.description}>
                        {(a.description || "").substring(0, 80)}
                        {(a.description || "").length > 80 ? "..." : ""}
                      </td>
                      <td>{a.responsable_name || "—"}</td>
                      <td>
                        <div className="tag-list">
                          {a.tags?.map((tag) => (
                            <span key={tag.id} className="tag-inline">{tag.label}</span>
                          ))}
                        </div>
                      </td>
                      <td className="row-actions">
                        {canEdit && (
                          <>
                            <button className="btn btn-small" onClick={() => navigate(`/actions/${a.id}/edit`)}>Modifier</button>
                            <button className="btn btn-small btn-danger" onClick={() => setDeleteActionId(a.id)}>Supprimer</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialogs */}
      {showDelete && (
        <ConfirmDialog
          message="Supprimer ce contact et toutes ses données associées ?"
          onConfirm={handleDeleteContact}
          onCancel={() => setShowDelete(false)}
        />
      )}
      {deleteTodoId && (
        <ConfirmDialog
          message="Supprimer ce To Do ?"
          onConfirm={handleDeleteTodo}
          onCancel={() => setDeleteTodoId(null)}
        />
      )}
      {deleteActionId && (
        <ConfirmDialog
          message="Supprimer cette action réalisée ?"
          onConfirm={handleDeleteAction}
          onCancel={() => setDeleteActionId(null)}
        />
      )}
    </div>
  );
}

function DetailField({ label, value, isLink }) {
  return (
    <div className="detail-field">
      <span className="detail-label">{label}</span>
      {isLink && value ? (
        <a href={`mailto:${value}`}>{value}</a>
      ) : (
        <span className="detail-value">{value || "—"}</span>
      )}
    </div>
  );
}
